import { WarningCircle } from "@phosphor-icons/react";

import AccountsTag, {
  type AccountsTagVariantType,
} from "common/components/ui/AccountsTag";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import { useCooldownStatesForAccounts } from "common/datahooks/useCooldownState";
import { getAccountStateLabel } from "common/helpers/utils";

import type { ReactNode } from "react";

interface WorkerAccountsTagProps {
  size: "sm" | "md";
  owners: string[];
  isUsingNote: boolean;
}

export default function WorkerAccountsTag({
  size,
  owners,
  isUsingNote,
}: WorkerAccountsTagProps) {
  const { accounts } = useSelectedWorkspaceContext();

  const workerAccounts = accounts.filter(({ id }) => owners.includes(id));

  const { cooldownStates } = useCooldownStatesForAccounts(
    workerAccounts.map(({ id }) => id),
  );

  const invalidWorkerAccounts = workerAccounts.filter(
    ({ id, state, license, premium, type }) => {
      const isMissingPremium = isUsingNote && !premium;
      const isMissingLicense = type === "user" && !license;
      const isLoggedOut = state !== "LoggedIn";
      const isInCooldown =
        cooldownStates[id]?.connect || cooldownStates[id]?.inmail;

      return (
        isMissingPremium || isMissingLicense || isLoggedOut || isInCooldown
      );
    },
  );

  let rightComponent: JSX.Element;
  let tagVariant: AccountsTagVariantType = "purple";
  let label = "No accounts";
  let tooltipContent: ReactNode;

  if (
    invalidWorkerAccounts.length === workerAccounts.length ||
    !workerAccounts.length
  ) {
    tagVariant = "disabled";
  } else if (invalidWorkerAccounts.length) {
    tagVariant = "warning";
    rightComponent = <WarningCircle weight="fill" />;
  }

  if (workerAccounts.length > 1) {
    label = `${owners.length} accounts`;

    const stateLabels = workerAccounts.map((account) => {
      const { id } = account;
      const isInCooldown =
        cooldownStates[id]?.connect || cooldownStates[id]?.inmail;

      return getAccountStateLabel(account, isUsingNote, isInCooldown);
    });

    const isAllOk = stateLabels.every((label) => !label);

    tooltipContent = workerAccounts.map((account, index) => {
      const { id, full_name } = account;
      const stateLabel = stateLabels[index];

      return (
        <span key={id}>
          {full_name}
          {!isAllOk && `: ${stateLabel || "OK"}`}
          <br />
        </span>
      );
    });
  } else if (workerAccounts.length === 1) {
    const account = workerAccounts[0];
    const { id, full_name } = account;

    label = full_name;

    const isInCooldown =
      cooldownStates[id]?.connect || cooldownStates[id]?.inmail;

    const stateLabel = getAccountStateLabel(account, isUsingNote, isInCooldown);
    if (stateLabel) {
      tooltipContent = stateLabel;
    }
  } else {
    tooltipContent = "There are no sender accounts in this campaign";
  }

  return (
    <AccountsTag
      accounts={workerAccounts}
      isLoading={false}
      tooltipContent={tooltipContent}
      variant={tagVariant}
      size={size}
      rightComponent={rightComponent}
    >
      {label}
    </AccountsTag>
  );
}
